import React, { useState, useEffect } from 'react';
import { 
    generateSecureReadUrlAPI, generateSecureUploadUrlAPI,
    addTicketCommentAPI, assignTicketAPI, updateTicketPriorityAPI,
    updateTicketServerIdAPI, assignFollowUpAPI, updateTicketFollowUpAPI,
    updateTicketCollaboratorsAPI 
} from '../services/serverService';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { FaDownload, FaEdit, FaPaperclip, FaTimes } from 'react-icons/fa';

export default function TicketDetailModal({ ticket, onClose, currentUser, staff, supportStaff, onTicketUpdated }) {
    const [comments, setComments] = useState([]);
    const [ticketLogs, setTicketLogs] = useState([]);
    const [activeRightTab, setActiveRightTab] = useState('conversation');
    
    const [newComment, setNewComment] = useState('');
    const [isInternal, setIsInternal] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    
    const [editingServerId, setEditingServerId] = useState(false);
    const [newServerIdInput, setNewServerIdInput] = useState('');
    const [followUpText, setFollowUpText] = useState('');
    const [isFollowUpUpdating, setIsFollowUpUpdating] = useState(false);

    // Identify if there is an active QA Follow-up OR a preserved historical one
    const activeQA = ticket.followUp;
    const historicalQA = ticket.followUpHistory?.[ticket.followUpHistory.length - 1];
    const qaData = activeQA || historicalQA;

    useEffect(() => {
        let unsubComments;
        let unsubLogs;
        
        if (ticket?.id) {
            if (activeQA && activeQA.status !== 'completed') {
                setActiveRightTab('qa');
            }

            // Real-time comments
            const commentsRef = collection(db, 'supportTickets', ticket.id, 'comments');
            unsubComments = onSnapshot(query(commentsRef, orderBy('createdAt', 'asc')), (snap) => {
                setComments(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            });

            // Real-time logs
            const logsRef = collection(db, 'supportTickets', ticket.id, 'ticketLogs');
            unsubLogs = onSnapshot(query(logsRef, orderBy('timestamp', 'desc')), (snap) => {
                setTicketLogs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            });
        }
        
        return () => {
            if (unsubComments) unsubComments();
            if (unsubLogs) unsubLogs();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticket?.id]);

    const handleAttachmentClick = async (fileUrl) => {
        if (!fileUrl) return;
        if (fileUrl.includes('firebasestorage.googleapis.com') && fileUrl.includes('alt=media')) {
            window.open(fileUrl, '_blank');
            return;
        }
        try {
            const res = await generateSecureReadUrlAPI(fileUrl);
            window.open(res.data.data.readUrl, '_blank');
        } catch (error) { alert("Failed to securely open file. It may have expired."); }
    };

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        setIsUploading(true);
        try {
            const newAttachments = [];
            for (const file of files) {
                const res = await generateSecureUploadUrlAPI({ fileName: file.name, fileType: file.type, fileSize: file.size });
                const { uploadUrl, fileUrl, mimeType, fileName } = res.data.data;
                await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': mimeType }, body: file });
                newAttachments.push({ fileName, fileUrl, mimeType, uploadedAt: new Date().toISOString() });
            }
            setAttachments(prev => [...prev, ...newAttachments]);
        } catch (err) { alert(err.message || 'File upload failed.'); } 
        finally { setIsUploading(false); }
    };

    const handleSendReply = async () => {
        if (!newComment.trim() && attachments.length === 0) return;
        setIsSending(true);
        try {
            await addTicketCommentAPI(ticket.id, newComment, isInternal, attachments);
            setNewComment('');
            setAttachments([]);
            setIsInternal(false);
        } catch (error) { alert("Failed to send reply."); } 
        finally { setIsSending(false); }
    };

    const handleAddCollaborator = async (e) => {
        const uid = e.target.value;
        if (!uid) return;
        const currentCollabs = ticket.collaborators || [];
        if (currentCollabs.includes(uid)) return; 

        const newCollabs = [...currentCollabs, uid];
        try {
            onTicketUpdated({ ...ticket, collaborators: newCollabs });
            await updateTicketCollaboratorsAPI(ticket.id, newCollabs);

        } catch (error) { alert("Failed to add collaborator."); }
    };

    const handleRemoveCollaborator = async (uid) => {
        const newCollabs = (ticket.collaborators || []).filter(id => id !== uid);
        try {
            onTicketUpdated({ ...ticket, collaborators: newCollabs });
            await updateTicketCollaboratorsAPI(ticket.id, newCollabs);

        } catch (error) { alert("Failed to remove collaborator."); }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Urgent': return '#e53e3e';
            case 'High': return '#ed8936';
            case 'Medium': return '#3182ce';
            default: return '#718096';
        }
    };

    const handleExportChat = () => {
        let content = `Ticket: ${ticket.ticketId} - ${ticket.subject}\nCategory: ${ticket.category}\nStatus: ${ticket.status}\n\n`;
        content += `--- ORIGINAL REQUEST ---\n${ticket.description}\n\n--- CONVERSATION HISTORY ---\n\n`;
        comments.forEach(c => {
            const author = c.createdByUid === 'CUSTOMER' ? 'Customer' : (c.createdByEmail?.split('@')[0] || 'Support Team');
            const ts = c.createdAt ? (c.createdAt.toDate ? c.createdAt.toDate().toLocaleString() : new Date(c.createdAt).toLocaleString()) : 'Unknown';
            content += `[${ts}] ${author} ${c.isInternalOnly ? '[INTERNAL]' : ''}:\n${c.message}\n\n`;
        });
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${ticket.ticketId}_Export.txt`;
        a.click();
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }} onClick={onClose}>
            {/* Modal Container: Fixed Layout so it doesn't squash */}
            <div style={{ backgroundColor: 'white', width: '1050px', maxWidth: '95%', height: '85vh', borderRadius: '8px', display: 'flex', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                
                {/* LEFT COLUMN: Metadata & Original Request - Independently Scrollable */}
                <div style={{ width: '350px', backgroundColor: '#f7fafc', borderRight: '1px solid #edf2f7', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h2 style={{ margin: 0, fontSize: '18px', color: '#2d3748' }}>{ticket.ticketId}</h2>
                            <span style={{ backgroundColor: getPriorityColor(ticket.priority), color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>{ticket.priority}</span>
                        </div>
                        <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#1a202c' }}>{ticket.subject}</h3>

                        <div style={{ marginBottom: '20px' }}>
                            <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#a0aec0', fontWeight: 'bold' }}>REPORTER</p>
                            <p style={{ margin: 0, fontSize: '14px', color: '#2d3748', fontWeight: 'bold' }}>{ticket.reporter?.username}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '13px', color: '#4a5568' }}>
                                <span>Server:</span>
                                {editingServerId ? (
                                    <div style={{ display: 'flex', gap: '5px' }}>
                                        <input value={newServerIdInput} onChange={e => setNewServerIdInput(e.target.value)} style={{ padding: '2px 6px', fontSize: '12px', width: '100px', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                                        <button onClick={async () => {
                                            await updateTicketServerIdAPI(ticket.id, newServerIdInput);
                                            onTicketUpdated({ ...ticket, reporter: { ...ticket.reporter, serverId: newServerIdInput } });
                                            setEditingServerId(false);
                                
                                        }} style={{ fontSize: '11px', padding: '2px 8px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '3px' }}>Save</button>
                                        <button onClick={() => setEditingServerId(false)} style={{ fontSize: '11px', padding: '2px 8px', backgroundColor: '#e2e8f0', border: 'none', borderRadius: '3px' }}>Cancel</button>
                                    </div>
                                ) : (
                                    <>
                                        <span style={{ backgroundColor: '#edf2f7', padding: '2px 6px', borderRadius: '4px' }}>{ticket.reporter?.serverId || 'N/A'}</span>
                                        <FaEdit style={{ cursor: 'pointer', color: '#a0aec0' }} onClick={() => { setNewServerIdInput(ticket.reporter?.serverId || ''); setEditingServerId(true); }} />
                                    </>
                                )}
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#a0aec0', fontWeight: 'bold' }}>ASSIGNED TO</p>
                            <select 
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                                value={ticket.assignedTo?.uid || ''}
                                onChange={async (e) => {
                                    const val = e.target.value;
                                    const staffMember = staff.find(s => s.uid === val);
                                    const newAssignee = staffMember ? { uid: staffMember.uid, name: staffMember.name } : null;
                                    await assignTicketAPI(ticket.id, val || 'unassigned', newAssignee?.name);
                                    onTicketUpdated({ ...ticket, assignedTo: newAssignee });
                        
                                }}
                            >
                                <option value="">-- Unassigned --</option>
                                {staff.map(user => <option key={user.uid} value={user.uid}>{user.name} ({user.role})</option>)}
                            </select>
                        </div>

                        {/* COLLABORATORS SECTION */}
                        <div style={{ marginBottom: '20px' }}>
                            <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#a0aec0', fontWeight: 'bold' }}>COLLABORATORS</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '8px' }}>
                                {(ticket.collaborators || []).map(uid => {
                                    const collabUser = staff.find(s => s.uid === uid) || { name: 'Unknown' };
                                    return (
                                        <span key={uid} style={{ fontSize: '11px', backgroundColor: '#e2e8f0', color: '#4a5568', padding: '2px 8px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            {collabUser.name.split(' ')[0]} 
                                            <FaTimes style={{ cursor: 'pointer', color: '#a0aec0' }} onClick={() => handleRemoveCollaborator(uid)} />
                                        </span>
                                    );
                                })}
                            </div>
                            <select 
                                style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e0', backgroundColor: 'white', fontSize: '12px' }}
                                value=""
                                onChange={handleAddCollaborator}
                            >
                                <option value="" disabled>+ Add Collaborator...</option>
                                {staff.filter(s => !(ticket.collaborators || []).includes(s.uid)).map(user => <option key={user.uid} value={user.uid}>{user.name}</option>)}
                            </select>
                        </div>

                        <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', backgroundColor: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                            <div style={{ flex: 1 }}>
                                <p style={{ margin: '0 0 5px 0', fontSize: '11px', color: '#2b6cb0', fontWeight: 'bold', textTransform: 'uppercase' }}>Internal Priority</p>
                                <select 
                                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '2px solid #3182ce', fontWeight: 'bold' }}
                                    value={ticket.internalPriority || ticket.priority || 'Medium'}
                                    onChange={async (e) => {
                                        await updateTicketPriorityAPI(ticket.id, e.target.value);
                                        onTicketUpdated({ ...ticket, internalPriority: e.target.value });
                            
                                    }}
                                >
                                    <option value="Untriaged">Untriaged</option>
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                    <option value="Urgent">Urgent</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#a0aec0', fontWeight: 'bold' }}>ORIGINAL DESCRIPTION</p>
                            <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#4a5568', whiteSpace: 'pre-wrap' }}>
                                {ticket.description}
                                {ticket.attachments?.length > 0 && (
                                    <ul style={{ paddingLeft: '20px', marginTop: '15px', borderTop: '1px solid #edf2f7', paddingTop: '10px' }}>
                                        {ticket.attachments.map((att, i) => (
                                            <li key={i}><span onClick={() => handleAttachmentClick(att.fileUrl || att.url)} style={{ color: '#3182ce', cursor: 'pointer', textDecoration: 'underline' }}>{att.fileName || att.name}</span></li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT COLUMN: Tabs (Chat / Logs / QA) */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: 'white', overflow: 'hidden' }}>
                    <div style={{ padding: '15px 20px', borderBottom: '1px solid #edf2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '25px', alignItems: 'center' }}>
                            <h3 onClick={() => setActiveRightTab('conversation')} style={{ margin: 0, fontSize: '16px', cursor: 'pointer', color: activeRightTab === 'conversation' ? '#2b6cb0' : '#a0aec0', borderBottom: activeRightTab === 'conversation' ? '2px solid #2b6cb0' : 'none', paddingBottom: '5px' }}>Conversation</h3>
                            <h3 onClick={() => setActiveRightTab('logs')} style={{ margin: 0, fontSize: '16px', cursor: 'pointer', color: activeRightTab === 'logs' ? '#e53e3e' : '#a0aec0', borderBottom: activeRightTab === 'logs' ? '2px solid #e53e3e' : 'none', paddingBottom: '5px' }}>Audit Logs</h3>
                            
                            {/* QA TAB (Only visible if Follow-up exists in active OR history) */}
                            {qaData && (
                                <h3 onClick={() => setActiveRightTab('qa')} style={{ margin: 0, fontSize: '16px', cursor: 'pointer', color: activeRightTab === 'qa' ? '#c53030' : '#a0aec0', borderBottom: activeRightTab === 'qa' ? '2px solid #c53030' : 'none', paddingBottom: '5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    QA Follow-up
                                    {activeQA && activeQA.status !== 'completed' && <span style={{ backgroundColor: '#e53e3e', color: 'white', fontSize: '9px', padding: '2px 6px', borderRadius: '10px', fontWeight: 'bold' }}>ACTION NEEDED</span>}
                                </h3>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '15px' }}>
                            {activeRightTab === 'conversation' && <button onClick={handleExportChat} style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 'bold', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', cursor: 'pointer' }}><FaDownload /> Export</button>}
                            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#a0aec0' }}>×</button>
                        </div>
                    </div>

                    {/* TAB CONTENT: QA Follow-up */}
                    {activeRightTab === 'qa' && qaData && (
                        <div style={{ flex: 1, padding: '30px', overflowY: 'auto', backgroundColor: '#fff5f5' }}>
                            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', border: '1px solid #fed7d7', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <h2 style={{ margin: 0, color: '#c53030' }}>⚠️ Escalated QA Review</h2>
                                    <span style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', backgroundColor: qaData.status === 'completed' ? '#c6f6d5' : '#feebc8', color: qaData.status === 'completed' ? '#22543d' : '#9c4221', fontWeight: 'bold', textTransform: 'uppercase' }}>
                                        {qaData.status}
                                    </span>
                                </div>
                                <div style={{ marginBottom: '20px', fontSize: '15px', color: '#4a5568', backgroundColor: '#f7fafc', padding: '15px', borderRadius: '6px', borderLeft: '4px solid #cbd5e0' }}>
                                    <strong>Trigger Reason:</strong> <br/> {qaData.reason}
                                </div>

                                {/* Read-only mode if historical, interactive if active */}
                                {!historicalQA ? (
                                    <>
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#718096', display: 'block', marginBottom: '8px' }}>Assign QA Investigator:</label>
                                            <select 
                                                value={qaData.assignee?.uid || ''} 
                                                onChange={async (e) => {
                                                    const uid = e.target.value;
                                                    const name = e.target.options[e.target.selectedIndex].text;
                                                    await assignFollowUpAPI({ ticketId: ticket.id, assigneeUid: uid, assigneeName: uid ? name : null });
                                                    onTicketUpdated({ ...ticket, followUp: { ...qaData, assignee: uid ? { uid, name } : null } });
                                        
                                                }}
                                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '14px' }}
                                            >
                                                <option value="">-- Unassigned --</option>
                                                {supportStaff.map(staff => <option key={staff.uid} value={staff.uid}>{staff.name} ({staff.role})</option>)}
                                            </select>
                                        </div>

                                        {qaData.action ? (
                                            <div style={{ marginBottom: '20px', fontSize: '14px', color: '#2b6cb0', padding: '15px', backgroundColor: '#ebf8ff', borderRadius: '6px' }}>
                                                <strong>Action Taken:</strong> {qaData.action}
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexDirection: 'column' }}>
                                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#718096' }}>Log Corrective Action:</label>
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <input type="text" placeholder="What action was taken to fix this?" value={followUpText} onChange={e => setFollowUpText(e.target.value)} style={{ flex: 1, padding: '10px', border: '1px solid #cbd5e0', borderRadius: '6px' }} />
                                                    <button 
                                                        disabled={!followUpText.trim() || isFollowUpUpdating} 
                                                        onClick={async () => {
                                                            setIsFollowUpUpdating(true);
                                                            await updateTicketFollowUpAPI({ ticketId: ticket.id, updateType: 'action', payloadText: followUpText });
                                                            onTicketUpdated({ ...ticket, followUp: { ...qaData, action: followUpText, status: 'in-progress' } });
                                                            setFollowUpText('');
                                                            setIsFollowUpUpdating(false);
                                                
                                                        }}
                                                        style={{ padding: '10px 20px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                                                    >Log Action</button>
                                                </div>
                                            </div>
                                        )}

                                        {qaData.action && !qaData.outcome && (
                                            <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#718096' }}>Log Final Outcome:</label>
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <input type="text" placeholder="What was the final result?" value={followUpText} onChange={e => setFollowUpText(e.target.value)} style={{ flex: 1, padding: '10px', border: '1px solid #cbd5e0', borderRadius: '6px' }} />
                                                    <button 
                                                        disabled={!followUpText.trim() || isFollowUpUpdating} 
                                                        onClick={async () => {
                                                            setIsFollowUpUpdating(true);
                                                            await updateTicketFollowUpAPI({ ticketId: ticket.id, updateType: 'outcome', payloadText: followUpText });
                                                            onTicketUpdated({ ...ticket, followUp: { ...qaData, outcome: followUpText, status: 'completed' } });
                                                            setFollowUpText('');
                                                            setIsFollowUpUpdating(false);
                                                
                                                        }}
                                                        style={{ padding: '10px 20px', backgroundColor: '#38a169', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                                                    >Resolve QA</button>
                                                </div>
                                            </div>
                                        )}

                                        {qaData.outcome && (
                                            <div style={{ fontSize: '14px', color: '#276749', padding: '15px', backgroundColor: '#f0fff4', borderRadius: '6px' }}>
                                                <strong>Final Outcome:</strong> {qaData.outcome}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    /* Historical Read-Only View */
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div style={{ padding: '10px', backgroundColor: '#edf2f7', borderRadius: '6px' }}><strong>Investigator:</strong> {qaData.assignee?.name || 'Unassigned'}</div>
                                        <div style={{ padding: '10px', backgroundColor: '#edf2f7', borderRadius: '6px' }}><strong>Action:</strong> {qaData.action || 'None recorded'}</div>
                                        <div style={{ padding: '10px', backgroundColor: '#edf2f7', borderRadius: '6px' }}><strong>Outcome:</strong> {qaData.outcome || 'None recorded'}</div>
                                        <div style={{ fontSize: '11px', color: '#718096', fontStyle: 'italic', marginTop: '10px' }}>Archived on {qaData.archivedAt ? (qaData.archivedAt.toDate ? qaData.archivedAt.toDate().toLocaleString() : new Date(qaData.archivedAt).toLocaleString()) : ''}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB CONTENT: Conversation */}
                    {activeRightTab === 'conversation' && (
                        <>
                            <div style={{ flex: 1, padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                {comments.map(comment => {
                                    const isCustomer = comment.createdByUid === 'CUSTOMER';
                                    return (
                                        <div key={comment.id} style={{ alignSelf: isCustomer ? 'flex-start' : 'flex-end', maxWidth: '80%' }}>
                                            <div style={{ padding: '12px 16px', borderRadius: '8px', backgroundColor: comment.isInternalOnly ? '#fefcbf' : (isCustomer ? '#f0fff4' : '#ebf8ff'), border: comment.isInternalOnly ? '1px solid #ecc94b' : (isCustomer ? '1px solid #c6f6d5' : '1px solid #bee3f8'), fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                                                {comment.isInternalOnly && <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#b7791f', marginBottom: '6px' }}>🔒 INTERNAL NOTE</div>}
                                                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#718096', marginBottom: '4px' }}>
                                                    {comment.createdByEmail || (isCustomer ? 'Customer' : 'Support')} • {comment.createdAt ? (comment.createdAt.toDate ? comment.createdAt.toDate().toLocaleString() : new Date(comment.createdAt).toLocaleString()) : ''}
                                                </div>
                                                {comment.message}
                                                {comment.attachments?.map((att, i) => (
                                                    <div key={i}>📎 <span onClick={() => handleAttachmentClick(att.fileUrl || att.url)} style={{ color: '#3182ce', cursor: 'pointer', textDecoration: 'underline' }}>{att.fileName || att.name}</span></div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div style={{ padding: '20px', borderTop: '1px solid #edf2f7', backgroundColor: '#f8fafc' }}>
                                <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Type reply..." style={{ width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e0', minHeight: '80px', marginBottom: '10px' }} />
                                
                                {isUploading && <div style={{ fontSize: '12px', color: '#3182ce', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>Uploading securely...</div>}
                                {attachments.length > 0 && (
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                        {attachments.map((att, i) => (
                                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: '#e2e8f0', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                                                📎 <span style={{ color: '#4a5568' }}>{att.fileName || att.name}</span>
                                                <span onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} style={{ cursor: 'pointer', color: '#e53e3e', fontWeight: 'bold', marginLeft: '5px', fontSize: '14px' }}>&times;</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', gap: '15px' }}>
                                        <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', fontSize: '14px' }}><input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} /> Internal Note</label>
                                        <label style={{ display: 'flex', gap: '5px', cursor: 'pointer', fontSize: '14px' }}><FaPaperclip /> Attach<input type="file" multiple onChange={handleFileUpload} style={{ display: 'none' }} disabled={isUploading} /></label>
                                    </div>
                                    <button onClick={handleSendReply} disabled={isSending || isUploading || (!newComment.trim() && !attachments.length)} style={{ padding: '10px 24px', backgroundColor: isInternal ? '#d69e2e' : '#3182ce', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', opacity: (isSending || isUploading) ? 0.7 : 1 }}>{isSending ? 'Sending...' : 'Send'}</button>
                                </div>
                            </div>
                        </>
                    )}

                    {/* TAB CONTENT: Audit Logs */}
                    {activeRightTab === 'logs' && (
                        <div style={{ flex: 1, padding: '20px', overflowY: 'auto', backgroundColor: '#fdfdfd' }}>
                            {ticketLogs.map(log => (
                                <div key={log.id} style={{ display: 'flex', gap: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', borderLeft: '4px solid #cbd5e0', marginBottom: '15px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                    <div style={{ fontSize: '12px', color: '#718096', minWidth: '140px', fontWeight: 'bold' }}>
                                        {log.timestamp ? (log.timestamp.toDate ? log.timestamp.toDate().toLocaleString() : new Date(log.timestamp).toLocaleString()) : ''}
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#2d3748', textTransform: 'uppercase' }}>{log.actionType.replace(/_/g, ' ')}</div>
                                        <div style={{ fontSize: '14px', color: '#4a5568' }}>{log.description}</div>
                                        <div style={{ fontSize: '11px', color: '#a0aec0', marginTop: '5px' }}>By: {log.actorName}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}